/**
 * CriticalAlertBanner — top-of-map banner for active CRITICAL alerts.
 *
 * Phase 7B introduced this to replace the Phase 6 status-rail severity
 * breakdown ("3c / 7w / 12i"). That breakdown was always-on noise that
 * made the rail feel busy without helping operators. This banner is
 * the inverse: invisible 99% of the time, dramatic when it matters.
 *
 * Filter: severity = CRITICAL AND created within the last 5 minutes
 * (or not yet acknowledged). Multiple matches → render the most recent
 * + a "+N more" link to /reports.
 *
 * Click INVESTIGATE → flies the camera to the alert's track and routes
 * to /reports?alert={id} so the existing detail drawer opens. Click ✕
 * → dismiss locally for this session (the banner reappears if a new
 * critical fires; we don't try to acknowledge from here, that's the
 * drawer's job).
 */
import { useMemo, useState } from 'react';
import { Transition } from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { reportsApi, type LatestAlertRow } from '../../services/reportsApi';
import { useMapStore } from '../../store/mapStore';

const ACTIVE_WINDOW_MS = 5 * 60 * 1000;

function ageString(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return 'now';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ago`;
}

export default function CriticalAlertBanner() {
  const navigate = useNavigate();
  const flyTo = useMapStore((s) => s.flyTo);
  // Session-local dismiss list — keyed by alert id. Doesn't persist; a
  // new banner reappears for a different critical alert immediately.
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set());

  const query = useQuery({
    queryKey: ['c2', 'critical-banner'],
    queryFn: async () => {
      const to = new Date();
      // Pull a 1h window so the "not yet acknowledged" branch can find
      // older alerts that still need attention. Filter client-side.
      const from = new Date(to.getTime() - 60 * 60 * 1000);
      const summary = await reportsApi.getSummary({
        from: from.toISOString(),
        to: to.toISOString(),
      });
      return summary.latestAlerts;
    },
    refetchInterval: 15_000,
    staleTime: 10_000,
  });

  const active = useMemo<LatestAlertRow[]>(() => {
    if (!query.data) return [];
    const now = Date.now();
    return query.data.filter((a) => {
      if (a.severity !== 'CRITICAL') return false;
      if (dismissed.has(a.id)) return false;
      const fresh = now - new Date(a.createdAt).getTime() < ACTIVE_WINDOW_MS;
      return fresh || !a.acknowledged;
    });
  }, [query.data, dismissed]);

  const head = active[0];
  const extra = active.length - 1;

  const handleInvestigate = () => {
    if (!head) return;
    // We don't have lat/lng on the alert payload — the Reports drawer
    // will load full track detail. flyTo by track id isn't supported
    // either, so we just navigate. Future enhancement: include
    // coordinates on the LatestAlertRow so we can fly first.
    navigate(`/reports?alert=${head.id}`);
    void flyTo; // mark as referenced for future use
  };

  return (
    <Transition
      show={!!head}
      enter="transition-all duration-300 ease-out"
      enterFrom="opacity-0 -translate-y-full"
      enterTo="opacity-100 translate-y-0"
      leave="transition-all duration-200 ease-in"
      leaveFrom="opacity-100 translate-y-0"
      leaveTo="opacity-0 -translate-y-full"
    >
      {head && (
        <div
          role="alert"
          className={clsx(
            'flex items-center gap-3 px-4 h-10 select-none',
            'bg-gradient-to-r from-alert/95 via-alert/85 to-alert/70',
            'border-b border-alert/60 text-white shadow-lg shadow-alert/10',
          )}
        >
          <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 animate-pulse-dot" />
          <span className="text-[10px] font-mono font-bold uppercase tracking-[0.2em] flex-shrink-0">
            CRITICAL
          </span>
          <span className="h-3.5 w-px bg-white/30" />
          <span className="text-[12px] font-semibold truncate" title={head.title}>
            {head.title}
          </span>
          <span className="text-[11px] text-white/80 truncate hidden md:inline">
            · {head.type}
            {head.zoneName ? ` · ${head.zoneName}` : ''}
            {head.droneModel ? ` · ${head.droneModel}` : ''}
          </span>
          <span className="text-[11px] text-white/70 font-mono ml-auto flex-shrink-0">
            {ageString(head.createdAt)}
          </span>
          {extra > 0 && (
            <button
              type="button"
              onClick={() => navigate('/reports?severity=CRITICAL')}
              className="text-[11px] font-mono font-semibold uppercase tracking-wider px-2 py-0.5 rounded-sm bg-white/15 hover:bg-white/25 transition-colors flex-shrink-0"
              title={`${extra} more critical alert${extra > 1 ? 's' : ''}`}
            >
              +{extra} more
            </button>
          )}
          <button
            type="button"
            onClick={handleInvestigate}
            className="text-[11px] font-mono font-bold uppercase tracking-wider px-3 py-1 rounded-sm bg-white text-alert hover:bg-white/90 transition-colors flex-shrink-0"
          >
            Investigate
          </button>
          <button
            type="button"
            onClick={() => setDismissed((d) => new Set(d).add(head.id))}
            title="Dismiss this banner (alert remains open)"
            className="text-white/70 hover:text-white p-1 flex-shrink-0"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      )}
    </Transition>
  );
}
